<script lang="ts" setup>
import { computed } from 'vue';

import Button from '@/components/Button.vue';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useLocale } from '@sigma/client/i18n/useLocale.js';
import { ChatStartRoute } from '@sigma/patient-common/routes.js';

import * as l from './HeroSection.loc.js';

/**
 * Optional copy overrides used by per-campaign landing variants. When a field is absent it falls back
 * to the default locale translation, so the standard landing page renders identically.
 */
interface Props {
  heading?: string;
  subtitle?: string;
  cta?: string;
}

const props = defineProps<Props>();

const t = useTranslate();
const locale = useLocale();

const heading = computed(() => props.heading ?? t(l.heroHeading));
const subtitle = computed(() => props.subtitle ?? t(l.heroSubtitle));
const cta = computed(() => props.cta ?? t(l.heroCta));
</script>

<template>
  <section
    id="start"
    :class="css.hero"
  >
    <div :class="css.content">
      <h1 :class="css.heading">
        {{ heading }}
      </h1>
      <p :class="css.subtitle">
        {{ subtitle }}
      </p>
      <Button
        :href="ChatStartRoute({ locale, agentUid: 'glp1-qualification' })"
        variant="primary"
        :class="css.cta"
      >
        {{ cta }}
      </Button>
    </div>
  </section>
</template>

<style lang="scss" module="css">
.hero {
  min-height: 95svh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--padding-global);
}

.content {
  max-width: var(--max-width-medium);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-xsmall);
}

.heading {
  font-size: var(--font-size-heading-0);
  font-weight: $font-weight-600;
  line-height: $line-height-heading-1;
  letter-spacing: $letter-spacing-heading-0;
}

.subtitle {
  color: $color-gray-700;
  font-size: var(--font-size-text-medium);
  line-height: 150%;
}

.cta {
  margin-top: var(--space-xsmall);
}
</style>
